import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import Index from "./pages/Index";
import Products from "./pages/Products";
import TrackOrder from "./pages/TrackOrder";
import NotFound from "./pages/NotFound";

// Lazy load admin pages for better initial bundle size
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminOrders = lazy(() => import("./pages/admin/AdminOrders"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminVouchers = lazy(() => import("./pages/admin/AdminVouchers"));
const AdminPaymentSettings = lazy(() => import("./pages/admin/AdminPaymentSettings"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminFAQs = lazy(() => import("./pages/admin/AdminFAQs"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));

const queryClient = new QueryClient();

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/products" element={<Products />} />
            <Route path="/track-order" element={<TrackOrder />} />
            
            {/* Admin Routes - Lazy loaded */}
            <Route path="/admin/login" element={<Suspense fallback={<PageLoader />}><AdminLogin /></Suspense>} />
            <Route path="/admin" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense></ProtectedRoute>} />
            <Route path="/admin/products" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminProducts /></Suspense></ProtectedRoute>} />
            <Route path="/admin/orders" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminOrders /></Suspense></ProtectedRoute>} />
            <Route path="/admin/categories" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminCategories /></Suspense></ProtectedRoute>} />
            <Route path="/admin/vouchers" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminVouchers /></Suspense></ProtectedRoute>} />
            <Route path="/admin/payment" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminPaymentSettings /></Suspense></ProtectedRoute>} />
            <Route path="/admin/testimonials" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminTestimonials /></Suspense></ProtectedRoute>} />
            <Route path="/admin/announcements" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminAnnouncements /></Suspense></ProtectedRoute>} />
            <Route path="/admin/faqs" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminFAQs /></Suspense></ProtectedRoute>} />
            <Route path="/admin/settings" element={<ProtectedRoute requireAdmin><Suspense fallback={<PageLoader />}><AdminSettings /></Suspense></ProtectedRoute>} />
            
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
